/* ==============================================================================
   Barra lateral
   ==============================================================================
   Dois modos, escolhidos pelo que está selecionado na dock:

     guild  →  canais de texto e de voz do servidor
     home   →  amigos e mensagens diretas

   São listas com naturezas diferentes e por isso não se misturam: canal
   pertence a um servidor e tem permissão por papel; conversa direta é entre
   pessoas e não tem papel nenhum. Juntar as duas numa lista só obrigaria cada
   item a carregar o próprio modelo de autorização, e a interface esconderia
   essa diferença justamente onde ela importa.

   Diferença visual em relação ao Discord: os canais de VOZ mostram quem está
   dentro embaixo do nome, sempre — não só ao entrar. Saber onde tem gente antes
   de clicar é a informação mais consultada num app de voz, e escondê-la atrás
   de um clique é o que faz a pessoa entrar em sala vazia.
   ============================================================================== */

import { useState, type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Avatar } from '../components/Avatar.tsx';
import { GuildIcon } from '../components/GuildIcon.tsx';

export interface SidebarChannel {
  id: string;
  name: string;
  type: 'text' | 'voice' | 'category' | 'forum' | 'announcement';
  /** Canal criptografado ponta a ponta. Ganha selo. */
  e2ee?: boolean;
  unread?: boolean;
  mentions?: number;
  /** Quem está no canal de voz. Vazio significa sala vazia. */
  participants?: ReadonlyArray<{ id: string; name: string }>;
}

export interface SidebarFriend {
  id: string;
  name: string;
  username: string;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  /** Mensagens não lidas na conversa direta. */
  unread?: number;
}

/* ==============================================================================
   Estado do amigo, em palavra e em cor
   ==============================================================================
   Os dois canais, sempre. A cor é lida de relance e a palavra é lida por quem
   não distingue as cores — e também por quem distingue mas não decorou qual
   amarelo significa "ausente".
============================================================================== */

const FRIEND_STATUS_LABEL: Record<SidebarFriend['status'], string> = {
  online: 'Online',
  idle: 'Ausente',
  dnd: 'Não perturbe',
  offline: 'Offline',
};

const FRIEND_STATUS_COLOR: Record<SidebarFriend['status'], string> = {
  online: 'var(--color-success)',
  idle: 'var(--color-warning)',
  dnd: 'var(--color-danger)',
  offline: 'var(--color-text-tertiary)',
};

export interface SidebarProps {
  mode: 'guild' | 'home';
  /** Nome exibido no topo: servidor ou "Início". */
  title: string;
  /**
   * Ícone do servidor, à esquerda do nome no cabeçalho.
   *
   * URL pronta para `<img src>`, ou `null` para cair nas iniciais. Aparece nos
   * três lugares em que o servidor é nomeado — cabeçalho da lateral, botão da
   * barra de comando e barra superior — porque o ícone só funciona como
   * identificador se for o MESMO em todos: um ícone que aparece na barra de baixo
   * e desaparece no cabeçalho não vira referência visual, vira decoração.
   */
  iconUrl?: string | null | undefined;
  channels?: ReadonlyArray<SidebarChannel>;
  activeChannelId?: string | null;
  onSelectChannel?: (id: string) => void;
  /**
   * Criar canal, com o TIPO.
   *
   * O "+" existia só ao lado de "Canais de texto", e num produto cuja razão de
   * ser é voz em grupo a única forma de criar uma sala de voz era abrir as
   * configurações do servidor. O tipo vem por argumento em vez de dois callbacks
   * separados porque o destino é o mesmo diálogo, só com o campo pré-escolhido.
   */
  onCreateChannel?: ((type: 'text' | 'voice') => void) | undefined;

  friends?: ReadonlyArray<SidebarFriend>;
  onSelectFriend?: ((id: string) => void) | undefined;
  /**
   * Abre a tela de amigos -- pedidos pendentes e busca de pessoa.
   *
   * NAO e mais um botao na lista. Virou a acao do cabecalho da secao, porque um
   * botao do tamanho de uma conversa, acima das conversas, era lido como uma
   * conversa: clicava-se nele esperando um chat.
   */
  onOpenFriends?: (() => void) | undefined;
  /**
   * Botao direito num amigo. Coordenadas de viewport.
   *
   * `onAddFriend` saiu: nao existe "nova conversa direta" para criar. A conversa
   * com alguem ja existe pelo fato de voces serem amigos, e abre no clique no
   * nome. O que faltava era a PESSOA, e isso e `onOpenFriends`.
   */
  onFriendContextMenu?:
    | ((friendId: string, x: number, y: number) => void)
    | undefined;

  /**
   * Envia pedido de amizade pelo nome digitado NA LATERAL.
   *
   * O campo fica aqui, e não só na tela de amigos, porque adicionar alguém é a
   * ação mais frequente desta coluna — e mandar a pessoa abrir outra tela para
   * digitar um nome é um desvio que ela faz várias vezes por sessão.
   */
  onSubmitFriendRequest?: ((username: string) => void) | undefined;

  /** Pedidos recebidos e enviados, para os dois contadores. */
  pendingIncoming?: number | undefined;
  pendingOutgoing?: number | undefined;

  /** Engrenagem do cabeçalho: preferências da conta. */
  onOpenSettings?: (() => void) | undefined;

  /** Recolhido mostra só ícones. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({
  mode,
  title,
  iconUrl,
  channels = [],
  activeChannelId,
  onSelectChannel,
  onCreateChannel,
  friends = [],
  onSelectFriend,
  onOpenFriends,
  onFriendContextMenu,
  onSubmitFriendRequest,
  pendingIncoming = 0,
  pendingOutgoing = 0,
  onOpenSettings,
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  return (
    <div className="flex h-full flex-col">
      {/* ------------------------------------------------------------------------
          Cabeçalho
      ------------------------------------------------------------------------ */}
      <header
        className="flex shrink-0 items-center justify-between gap-2 px-3"
        style={{
          minHeight: 44,
          boxShadow: 'inset 0 -1px 0 oklch(100% 0 0 / 0.055)',
        }}
      >
        {/* ----------------------------------------------------------------------
            Ícone + nome do servidor
            ----------------------------------------------------------------------
            O ícone aparece TAMBÉM no modo recolhido, e é o único conteúdo que
            sobra ali: com 52px de largura não cabe o nome, e um cabeçalho vazio
            faria a coluna recolhida parecer um painel quebrado.
        ---------------------------------------------------------------------- */}
        <div className="flex min-w-0 items-center" style={{ gap: 'var(--spacing-2)' }}>
          {mode === 'guild' && (
            <GuildIcon name={title} iconUrl={iconUrl ?? null} size={24} />
          )}
          {!collapsed && (
            <span
              className="truncate font-semibold"
              style={{
                fontSize: 'var(--text-sm)',
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--color-text-primary)',
              }}
            >
              {title}
            </span>
          )}
        </div>

        {/* ------------------------------------------------------------------
            Ações do cabeçalho, só no modo início
            ------------------------------------------------------------------
            Duas, como na referência: abrir a tela de amigos (pedidos, busca) e
            as preferências. Ficam no cabeçalho e não na lista porque são ações
            SOBRE a coluna, não itens dela — foi o erro do botão "Meus amigos",
            que tinha o peso visual de uma conversa e era clicado como uma.
        ------------------------------------------------------------------ */}
        {mode === 'home' && !collapsed && (
          <div className="flex shrink-0 items-center" style={{ gap: 2 }}>
            {onOpenFriends && (
              <HeaderIcon
                label="Amigos e pedidos"
                badge={pendingIncoming}
                onClick={onOpenFriends}
              >
                <circle cx="6" cy="5.4" r="2.4" stroke="currentColor" strokeWidth="1.4" />
                <path d="M1.8 13.2c0-2.3 1.9-3.7 4.2-3.7s4.2 1.4 4.2 3.7"
                      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <path d="M11.4 4.2a2.2 2.2 0 010 4.2M12.4 13.2c0-1.6-.5-2.6-1.3-3.3"
                      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </HeaderIcon>
            )}
            {onOpenSettings && (
              <HeaderIcon label="Configurações" onClick={onOpenSettings}>
                <circle cx="8" cy="8" r="2.1" stroke="currentColor" strokeWidth="1.4" />
                <path d="M8 1.6v1.8M8 12.6v1.8M1.6 8h1.8M12.6 8h1.8M3.5 3.5l1.3 1.3M11.2 11.2l1.3 1.3M12.5 3.5l-1.3 1.3M4.8 11.2l-1.3 1.3"
                      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </HeaderIcon>
            )}
          </div>
        )}

        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="grid size-6 shrink-0 place-items-center rounded transition-colors"
            style={{ color: 'var(--color-text-tertiary)' }}
            aria-label={collapsed ? 'Expandir barra lateral' : 'Recolher barra lateral'}
            aria-expanded={!collapsed}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d={collapsed ? 'M6 4l4 4-4 4' : 'M10 4L6 8l4 4'}
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </header>

      {/* ------------------------------------------------------------------------
          Corpo
      ------------------------------------------------------------------------ */}
      <div className="scroll-hover-only min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {mode === 'guild' ? (
          <GuildChannels
            channels={channels}
            activeChannelId={activeChannelId ?? null}
            collapsed={collapsed}
            onSelect={onSelectChannel}
            onCreate={onCreateChannel}
          />
        ) : (
          <HomeList
            friends={friends}
            collapsed={collapsed}
            activeChannelId={activeChannelId ?? null}
            onSelectFriend={onSelectFriend}
            onOpenFriends={onOpenFriends}
            pendingIncoming={pendingIncoming}
            pendingOutgoing={pendingOutgoing}
            {...(onFriendContextMenu ? { onFriendContextMenu } : {})}
            {...(onSubmitFriendRequest ? { onSubmitFriendRequest } : {})}
          />
        )}
      </div>
    </div>
  );
}

/* ==============================================================================
   Modo servidor: canais
   ============================================================================== */

function GuildChannels({
  channels,
  activeChannelId,
  collapsed,
  onSelect,
  onCreate,
}: {
  channels: ReadonlyArray<SidebarChannel>;
  activeChannelId: string | null;
  collapsed: boolean;
  onSelect?: ((id: string) => void) | undefined;
  onCreate?: ((type: 'text' | 'voice') => void) | undefined;
}) {
  const text = channels.filter(
    (c) => c.type === 'text' || c.type === 'announcement' || c.type === 'forum',
  );
  const voice = channels.filter((c) => c.type === 'voice');

  return (
    <>
      <Section
        label="Canais de texto"
        collapsed={collapsed}
        addLabel="Criar canal de texto"
        {...(onCreate ? { onAdd: () => onCreate('text') } : {})}
      >
        {text.length === 0 ? (
          <EmptyHint collapsed={collapsed}>Nenhum canal de texto</EmptyHint>
        ) : (
          text.map((channel) => (
            <ChannelRow
              key={channel.id}
              channel={channel}
              active={channel.id === activeChannelId}
              collapsed={collapsed}
              onClick={() => onSelect?.(channel.id)}
            />
          ))
        )}
      </Section>

      {/* O "+" AQUI é o item que faltava. Ver a nota em `onCreateChannel`. */}
      <Section
        label="Canais de voz"
        collapsed={collapsed}
        addLabel="Criar sala de voz"
        {...(onCreate ? { onAdd: () => onCreate('voice') } : {})}
      >
        {voice.length === 0 ? (
          <EmptyHint collapsed={collapsed}>Nenhuma sala de voz</EmptyHint>
        ) : (
          voice.map((channel) => (
            <ChannelRow
              key={channel.id}
              channel={channel}
              active={channel.id === activeChannelId}
              collapsed={collapsed}
              onClick={() => onSelect?.(channel.id)}
            />
          ))
        )}
      </Section>
    </>
  );
}

/* ==============================================================================
   Modo início: amigos e conversas diretas
   ============================================================================== */

function HomeList({
  friends,
  collapsed,
  activeChannelId,
  onSelectFriend,
  onOpenFriends,
  onFriendContextMenu,
  onSubmitFriendRequest,
  pendingIncoming = 0,
  pendingOutgoing = 0,
}: {
  friends: ReadonlyArray<SidebarFriend>;
  /*
   * `pendingInvites` foi SUBSTITUIDO por dois contadores.
   *
   * Um numero so nao diz se e pedido recebido (que pede acao) ou enviado (que
   * pede espera) -- e a referencia mostra os dois lado a lado justamente porque
   * sao coisas diferentes. Colapsar os dois faria o selo do cabecalho piscar
   * por um pedido que a pessoa mesma mandou.
   */
  onSubmitFriendRequest?: ((username: string) => void) | undefined;
  pendingIncoming?: number | undefined;
  pendingOutgoing?: number | undefined;
  collapsed: boolean;
  activeChannelId: string | null;
  onSelectFriend?: ((id: string) => void) | undefined;
  onOpenFriends?: (() => void) | undefined;
  onFriendContextMenu?:
    | ((friendId: string, x: number, y: number) => void)
    | undefined;
}) {
  /*
   * O botao "Meus amigos" foi REMOVIDO daqui.
   *
   * A versao anterior tinha uma pastilha de acento no topo, do tamanho de uma
   * linha de conversa, e abaixo dela a lista. O argumento era que "amigos e um
   * destino, nao uma conversa" -- e ele estava errado na pratica: um botao com o
   * peso visual de um item de lista, no topo de uma lista, e lido como o
   * primeiro item da lista. E a lista de amigos JA estava logo abaixo, entao o
   * botao levava a uma tela que repetia o que estava na tela.
   *
   * A tela de amigos continua existindo -- pedidos pendentes e busca de pessoa
   * nao cabem na lateral. Ela agora e a acao do CABECALHO da secao, o mesmo
   * lugar onde "+" cria um canal num servidor. Cabecalho e onde se procura acao
   * de gerenciar a lista; item de lista e onde se procura conteudo.
   *
   * O contador de pedidos pendentes viaja com ela, porque e a unica pista de que
   * ha algo para resolver la.
   */
  const online = friends.filter((f) => f.status !== 'offline');
  const offline = friends.filter((f) => f.status === 'offline');

  return (
    <>
      {/* ------------------------------------------------------------------
          Adicionar amigo, INLINE
          ------------------------------------------------------------------
          O campo fica na coluna porque adicionar alguém é a ação mais frequente
          daqui. Mandar abrir outra tela para digitar um nome é um desvio que a
          pessoa faz várias vezes por sessão.

          Recolhido ele sai: 52px não acomodam um campo de texto, e um input de
          uma letra de largura é pior que a ausência dele.
      ------------------------------------------------------------------ */}
      {!collapsed && onSubmitFriendRequest && (
        <AddFriendField onSubmit={onSubmitFriendRequest} />
      )}

      {/* ------------------------------------------------------------------
          Recebidos e enviados
          ------------------------------------------------------------------
          Os dois lado a lado, como na referência. Eles existem porque um pedido
          recebido só é descoberto por quem abre a tela de amigos por conta — e
          ninguém abre uma tela para conferir se há algo nela.

          Os dois são clicáveis e levam ao mesmo lugar: a tela de amigos é onde
          se responde e onde se acompanha. Separar em dois destinos seria
          inventar navegação para uma diferença que não existe.
      ------------------------------------------------------------------ */}
      {!collapsed && onOpenFriends && (
        <div className="mb-3 flex gap-2 px-2">
          <RequestCounter
            label="Recebidos"
            count={pendingIncoming}
            /* Sino: pedido recebido é algo que chega, e chegar é notificação. */
            path="M8 2.4a3.4 3.4 0 013.4 3.4v2.6l1.1 1.8H3.5l1.1-1.8V5.8A3.4 3.4 0 018 2.4zM6.4 12.8a1.6 1.6 0 003.2 0"
            highlight={pendingIncoming > 0}
            onClick={onOpenFriends}
          />
          <RequestCounter
            label="Enviados"
            count={pendingOutgoing}
            /* Seta: pedido enviado é algo que partiu, e a direção diz isso. */
            path="M2.8 8h9.2M8.8 4.4L12.4 8l-3.6 3.6"
            highlight={false}
            onClick={onOpenFriends}
          />
        </div>
      )}

      <Section label={`Amigos (${friends.length})`} collapsed={collapsed}>
        {/* --------------------------------------------------------------
            Rótulos de coluna
            --------------------------------------------------------------
            "Perfil" e "Estado", como na referência. Eles explicam a coluna da
            direita: um ponto verde sem rótulo é decoração, com rótulo é dado.
        -------------------------------------------------------------- */}
        {!collapsed && friends.length > 0 && (
          <div
            className="mb-0.5 flex items-center justify-between px-2"
            style={{
              fontSize: 'var(--text-2xs)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            <span>Perfil</span>
            <span>Estado</span>
          </div>
        )}

        {friends.length === 0 ? (
          <EmptyHint collapsed={collapsed}>
            Nenhum amigo ainda. Use o campo acima para enviar um convite.
          </EmptyHint>
        ) : (
          [...online, ...offline].map((friend) => (
            <FriendRow
              key={friend.id}
              friend={friend}
              active={friend.id === activeChannelId}
              collapsed={collapsed}
              onClick={() => onSelectFriend?.(friend.id)}
              {...(onFriendContextMenu
                ? {
                    onContextMenu: (x: number, y: number) =>
                      onFriendContextMenu(friend.id, x, y),
                  }
                : {})}
            />
          ))
        )}
      </Section>
    </>
  );
}

/* ==============================================================================
   Campo de adicionar amigo
   ==============================================================================
   Submete por Enter E por botão. Os dois porque o gesto natural depois de
   digitar um nome é Enter, e um campo que só aceita clique no botão obriga a
   soltar o teclado para completar uma ação de teclado.
============================================================================== */

function AddFriendField({ onSubmit }: { onSubmit: (username: string) => void }) {
  const [value, setValue] = useState('');

  const submit = () => {
    const nome = value.trim().replace(/^@/, '');
    /*
     * O `@` inicial é removido em silêncio.
     *
     * A interface mostra `@luna_art` em todo lugar, então é o que a pessoa
     * copia e cola. Recusar por causa dele seria punir alguém por seguir a
     * própria convenção da tela.
     */
    if (nome.length < 2) return;
    onSubmit(nome);
    setValue('');
  };

  const vazio = value.trim().replace(/^@/, '').length < 2;

  return (
    /* ------------------------------------------------------------------------
       Campo e botão num MESMO recipiente
       ------------------------------------------------------------------------
       Como na referência, e não dois retângulos vizinhos. O botão está dentro da
       borda do campo porque ele age SOBRE o que está no campo — separados, a
       leitura é de dois controles independentes, e "Enviar" sem objeto explícito
       ao lado de um campo vazio é uma pergunta em vez de uma ação.

       O `focus-within` acende a borda do recipiente inteiro: com o realce só no
       `<input>`, a borda externa ficaria apagada em volta de um campo aceso, e o
       foco pareceria estar num elemento menor do que o alvo de clique real.
    ------------------------------------------------------------------------ */
    <form
      className="mx-2 mb-2.5 mt-2 flex items-center gap-1.5 pl-2.5 pr-1 transition-shadow focus-within:shadow-none"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      style={{
        height: 36,
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-surface-sunken)',
        boxShadow:
          'inset 0 1px 0 oklch(0% 0 0 / 0.22), 0 0 0 1px var(--color-border-subtle)',
      }}
    >
      <input
        type="text"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Adicionar Amigo"
        aria-label="Nome de usuário do amigo"
        maxLength={32}
        className="min-w-0 flex-1 bg-transparent outline-none"
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-primary)',
        }}
      />
      <button
        type="submit"
        /*
         * Desabilitado com menos de 2 caracteres, em vez de aceitar e receber
         * erro do servidor. O mínimo é o mesmo do cadastro de nome de usuário.
         */
        disabled={vazio}
        className="shrink-0 px-3 font-semibold transition-colors"
        style={{
          height: 28,
          borderRadius: 'var(--radius-full)',
          background: vazio
            ? 'var(--color-surface-hover)'
            : 'var(--color-accent-solid)',
          color: vazio ? 'var(--color-text-disabled)' : 'var(--color-on-solid)',
          fontSize: 'var(--text-2xs)',
        }}
      >
        Enviar
      </button>
    </form>
  );
}

/* ==============================================================================
   Contador de pedidos
============================================================================== */

function RequestCounter({
  label,
  count,
  path,
  highlight,
  onClick,
}: {
  label: string;
  count: number;
  path: string;
  highlight: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="touch-row flex min-w-0 flex-1 items-center gap-2 px-1.5 py-1.5 text-left transition-colors"
      style={{
        borderRadius: 'var(--radius-md)',
        /*
         * Só o de RECEBIDOS ganha acento, e só quando há algo.
         *
         * Destacar os dois igualmente faria "enviados: 0" competir por atenção
         * com um pedido de verdade esperando resposta.
         */
        background: highlight ? 'var(--color-accent-subtle)' : 'transparent',
        color: highlight
          ? 'var(--color-accent-on-subtle)'
          : 'var(--color-text-tertiary)',
      }}
      onMouseEnter={(event) => {
        if (!highlight) event.currentTarget.style.background = 'oklch(100% 0 0 / 0.06)';
      }}
      onMouseLeave={(event) => {
        if (!highlight) event.currentTarget.style.background = 'transparent';
      }}
    >
      {/* --------------------------------------------------------------------
          O ícone num disco próprio
          --------------------------------------------------------------------
          Como na referência. O disco não é enfeite: ele dá ao ícone o mesmo peso
          e o mesmo diâmetro de um avatar, e é isso que faz os dois contadores
          lerem como uma FILEIRA e não como dois textos com um símbolo grudado.
      -------------------------------------------------------------------- */}
      <span
        aria-hidden="true"
        className="grid shrink-0 place-items-center"
        style={{
          /* 28: o mesmo diâmetro do avatar na lista de amigos, para as duas
             fileiras compartilharem a coluna da esquerda. */
          width: 28,
          height: 28,
          borderRadius: 'var(--radius-full)',
          background: highlight
            ? 'oklch(100% 0 0 / 0.10)'
            : 'var(--color-surface-sunken)',
          boxShadow: '0 0 0 1px var(--color-border-subtle)',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d={path} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
                strokeLinejoin="round" />
        </svg>
      </span>

      {/* --------------------------------------------------------------------
          Rótulo e contagem em DUAS linhas
          --------------------------------------------------------------------
          "Recebidos (0)" numa linha só, em duas colunas de ~110px, truncava
          para "Recebid…" — e um rótulo cortado antes do número não informa nem
          a categoria nem a quantidade. Empilhado, o rótulo cabe inteiro e a
          contagem ganha corpo próprio, que é o dado que se procura ali.
      -------------------------------------------------------------------- */}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate" style={{ fontSize: 'var(--text-2xs)' }}>
          {label}
        </span>
        <span
          className="font-semibold"
          style={{
            fontSize: 'var(--text-xs)',
            color: highlight
              ? 'var(--color-accent-on-subtle)'
              : 'var(--color-text-secondary)',
          }}
        >
          {count}
        </span>
      </span>
    </button>
  );
}

/* ==============================================================================
   Ícone de ação do cabeçalho
   ==============================================================================
   `badge` desenha o contador sobreposto. Ele existe porque o ícone de amigos é
   o único caminho para os pedidos recebidos, e um pedido que ninguém vê é um
   pedido que fica sem resposta.
============================================================================== */

function HeaderIcon({
  label,
  onClick,
  badge = 0,
  children,
}: {
  label: string;
  onClick: () => void;
  badge?: number | undefined;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="relative grid size-7 place-items-center transition-colors"
      style={{
        borderRadius: 'var(--radius-md)',
        color: 'var(--color-text-tertiary)',
      }}
      onMouseEnter={(event) => {
        event.currentTarget.style.background = 'oklch(100% 0 0 / 0.07)';
        event.currentTarget.style.color = 'var(--color-text-primary)';
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = 'transparent';
        event.currentTarget.style.color = 'var(--color-text-tertiary)';
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        {children}
      </svg>
      {badge !== undefined && badge > 0 && (
        <span
          className="absolute grid place-items-center font-bold"
          style={{
            top: -1,
            right: -1,
            minWidth: 14,
            height: 14,
            padding: '0 3px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-danger-solid)',
            color: 'var(--color-on-solid)',
            fontSize: 9,
            /* Anel da cor do painel: separa o selo do ícone sem borda visível. */
            boxShadow: '0 0 0 2px var(--color-surface-raised)',
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/* ==============================================================================
   Peças
   ============================================================================== */

function Section({
  label,
  collapsed,
  onAdd,
  addLabel,
  badge,
  children,
}: {
  label: string;
  collapsed: boolean;
  onAdd?: (() => void) | undefined;
  /**
   * Rótulo do botão de adicionar.
   *
   * Explícito e não derivado de `label`: "Adicionar em Canais de voz" é o que
   * uma concatenação produz, e é pior do que a frase que a pessoa espera ouvir
   * do leitor de tela — "Criar sala de voz".
   */
  addLabel?: string;
  /**
   * Contador ao lado do rotulo. `0` ou ausente nao desenha nada.
   *
   * Existe por causa dos pedidos de amizade pendentes: sem uma pista na lateral,
   * um pedido recebido so e descoberto por quem abre a tela de amigos por conta
   * -- e ninguem abre uma tela para conferir se ha algo nela.
   */
  badge?: number | undefined;
  children: ReactNode;
}) {
  return (
    <section className="mb-4">
      {!collapsed && (
        <div className="mb-1 flex items-center justify-between px-2">
          <h3
            className="font-semibold uppercase"
            style={{
              fontSize: 'var(--text-2xs)',
              letterSpacing: 'var(--tracking-wide)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            {label}
          </h3>
          <div className="flex items-center gap-1.5">
            {badge !== undefined && badge > 0 && (
              <span
                className="flex items-center justify-center px-1.5 font-bold"
                style={{
                  minWidth: 17,
                  height: 17,
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-danger-solid)',
                  color: 'var(--color-on-solid)',
                  fontSize: 10,
                }}
              >
                {badge}
                <span className="sr-only"> pedidos pendentes</span>
              </span>
            )}
            {onAdd && (
              <AddButton label={addLabel ?? `Adicionar em ${label}`} onClick={onAdd} />
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-0.5">{children}</div>
    </section>
  );
}

/**
 * Botão de adicionar de uma seção.
 *
 * Alvo de 22px com o traço de 12px dentro. O traço sozinho era o alvo antes, e
 * um "+" de 12px é frustrante de acertar — a área cresce, o desenho não.
 */
function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="grid shrink-0 place-items-center transition-colors"
      style={{
        width: 22,
        height: 22,
        borderRadius: 'var(--radius-sm)',
        background: hover ? 'oklch(100% 0 0 / 0.08)' : 'transparent',
        color: hover ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
        transitionDuration: 'var(--duration-fast)',
      }}
      aria-label={label}
      title={label}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M6 2.5v7M2.5 6h7" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" />
      </svg>
    </button>
  );
}

function EmptyHint({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: ReactNode;
}) {
  if (collapsed) return null;
  return (
    <p
      className="px-2 py-1"
      style={{
        fontSize: 'var(--text-2xs)',
        color: 'var(--color-text-tertiary)',
        lineHeight: 'var(--leading-relaxed)',
      }}
    >
      {children}
    </p>
  );
}

function ChannelRow({
  channel,
  active,
  collapsed,
  onClick,
}: {
  channel: SidebarChannel;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  const isVoice = channel.type === 'voice';
  const participants = channel.participants ?? [];

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-current={active ? 'page' : undefined}
        className="touch-row relative flex w-full items-center gap-2 py-1.5 pl-3 pr-2 transition-colors"
        style={{
          borderRadius: 'var(--radius-md)',
          /*
           * Ativo com PASTILHA DE ACENTO, e não com uma superfície mais clara.
           *
           * `--color-surface-active` é um retângulo cinza 28% mais claro que a
           * lateral, e era exatamente o "cartão cinza genérico" da crítica: ele
           * marca a linha sem dizer nada sobre ela. Pior, fica quase idêntico ao
           * hover (24%), então a lista mostrava dois "selecionados" ao mesmo
           * tempo — um sob o cursor e outro aberto.
           *
           * A pastilha translúcida de acento distingue os dois por MATIZ e não
           * por luminosidade, e o hover pode continuar sendo só luz.
           */
          background: active
            ? 'var(--color-accent-subtle)'
            : hover
              ? 'oklch(100% 0 0 / 0.07)'
              : 'transparent',
          /* Não lido ganha peso e cor de texto primário: é o sinal mais
             consultado da lista, e cor sozinha não basta. */
          color:
            active || channel.unread
              ? 'var(--color-text-primary)'
              : 'var(--color-text-tertiary)',
        }}
      >
        {/* Barra de acento na aresta esquerda do item aberto. Segundo canal
            para a mesma informação — a pastilha a 16% de alfa desaparece para
            quem tem baixa sensibilidade a contraste. */}
        {active && (
          <span
            aria-hidden="true"
            className="absolute rounded-full"
            style={{
              left: 3,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: 14,
              background: 'var(--color-accent)',
              boxShadow: '0 0 8px var(--color-accent-glow)',
            }}
          />
        )}

        <span aria-hidden="true" className="shrink-0"
              style={active ? { color: 'var(--color-accent-on-subtle)' } : undefined}>
          {isVoice ? <VoiceIcon /> : <HashIcon />}
        </span>

        {!collapsed && (
          <>
            <span
              className="min-w-0 flex-1 truncate text-left"
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: channel.unread ? 600 : 400,
              }}
            >
              {channel.name}
            </span>

            {channel.e2ee && (
              <span
                title="Criptografado ponta a ponta"
                aria-label="Criptografado ponta a ponta"
                className="shrink-0"
                style={{ color: 'var(--color-success)' }}
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <rect x="2.4" y="5.2" width="7.2" height="5" rx="1.2"
                        stroke="currentColor" strokeWidth="1.2" />
                  <path d="M4.2 5.2V3.9a1.8 1.8 0 013.6 0v1.3"
                        stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </span>
            )}

            {channel.mentions !== undefined && channel.mentions > 0 && (
              <span
                className="flex shrink-0 items-center justify-center px-1.5 font-bold"
                style={{
                  minWidth: 17,
                  height: 17,
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-danger-solid)',
                  color: 'var(--color-on-solid)',
                  fontSize: 10,
                }}
              >
                {channel.mentions > 99 ? '99+' : channel.mentions}
                <span className="sr-only"> menções</span>
              </span>
            )}
          </>
        )}
      </button>

      {/* ----------------------------------------------------------------------
          Quem está no canal de voz — SEMPRE visível, não só ao entrar.
          "Onde tem gente" é a informação mais consultada num app de voz;
          esconder atrás de um clique é o que faz a pessoa entrar em sala vazia.
      ---------------------------------------------------------------------- */}
      {isVoice && !collapsed && participants.length > 0 && (
        <ul className="mb-1 ml-6 mt-0.5 flex flex-col gap-1">
          {participants.map((person) => (
            <li key={person.id} className="flex items-center gap-1.5">
              <Avatar name={person.name} seed={person.id} size={16} />
              <span
                className="truncate"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {person.name}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FriendRow({
  friend,
  active,
  collapsed,
  onClick,
  onContextMenu,
}: {
  friend: SidebarFriend;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
  onContextMenu?: ((x: number, y: number) => void) | undefined;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      /*
       * O menu abre na LINHA inteira, nao so no avatar.
       *
       * O pedido falava do icone, e um alvo de 24px para botao direito e uma
       * mira difícil -- especialmente em trackpad, onde o gesto ja e secundario.
       * A linha toda e o alvo, e o avatar continua dentro dela.
       *
       * Sem `onContextMenu` a prop nao e registrada, e o menu nativo do
       * navegador continua funcionando -- que e o certo quando nao ha acao
       * propria a oferecer.
       */
      {...(onContextMenu
        ? {
            onContextMenu: (event: ReactMouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              onContextMenu(event.clientX, event.clientY);
            },
          }
        : {})}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-current={active ? 'page' : undefined}
      className="touch-row flex w-full items-center gap-2 px-2 py-1.5 transition-colors"
      style={{
        borderRadius: 'var(--radius-md)',
        /* Mesma decisão da linha de canal: acento no ativo, luz no hover. Ver a
           nota em `ChannelRow`. */
        background: active
          ? 'var(--color-accent-subtle)'
          : hover
            ? 'oklch(100% 0 0 / 0.07)'
            : 'transparent',
        /* Offline fica em texto terciário: a lista mantém todos, mas quem está
           disponível salta. Ocultar offline esconderia com quem se pode falar
           mesmo assim. */
        opacity: friend.status === 'offline' ? 0.55 : 1,
      }}
    >
      <Avatar
        name={friend.name}
        seed={friend.id}
        /*
         * 28 e não 30: `AvatarSize` é uma união fechada, e de propósito. Um
         * avatar de tamanho arbitrário quebra a grade — e a lista de amigos usa
         * o mesmo diâmetro dos discos dos contadores logo acima, que é o que faz
         * as duas fileiras se alinharem.
         */
        size={28}
        status={friend.status}
      />
      {!collapsed && (
        <>
          {/* ------------------------------------------------------------------
              Nome E `@usuário`, empilhados
              ------------------------------------------------------------------
              O nome sozinho não é endereço. Ele muda quando a pessoa quer, e não
              serve para mencionar nem para adicionar de novo — para isso é o
              `@usuário`, que não muda. Só o nome obrigava a abrir o perfil para
              descobrir como se dirige a alguém que já está na lista.

              O `@usuário` vem em texto terciário e um corpo abaixo: é dado de
              consulta, e no mesmo peso do nome os dois competiriam pela varredura
              vertical da coluna, que é feita por nome.
          ------------------------------------------------------------------ */}
          <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
            <span
              className="truncate"
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: friend.unread ? 600 : 500,
                color: friend.unread
                  ? 'var(--color-text-primary)'
                  : 'var(--color-text-secondary)',
              }}
            >
              {friend.name}
            </span>
            <span
              className="truncate"
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              @{friend.username}
            </span>
          </span>

          {/* ------------------------------------------------------------------
              Estado, à direita
              ------------------------------------------------------------------
              É a coluna que "Estado" nomeia no cabeçalho da seção — e sem ela o
              rótulo apontava para nada. O ponto do avatar já carrega a mesma
              informação, mas em cor apenas: quem não distingue verde de cinza
              não tinha nenhuma leitura do estado, e é a informação que decide se
              vale chamar agora.

              Some quando há não lidas: aí o selo vermelho ocupa este lugar, e o
              que importa é a mensagem esperando, não se a pessoa está online.
          ------------------------------------------------------------------ */}
          {(friend.unread === undefined || friend.unread === 0) && (
            <span
              className="flex shrink-0 items-center gap-1"
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              <span
                aria-hidden="true"
                className="block rounded-full"
                style={{
                  width: 6,
                  height: 6,
                  background: FRIEND_STATUS_COLOR[friend.status],
                }}
              />
              {FRIEND_STATUS_LABEL[friend.status]}
            </span>
          )}

          {friend.unread !== undefined && friend.unread > 0 && (
            <span
              className="flex shrink-0 items-center justify-center px-1.5 font-bold"
              style={{
                minWidth: 17,
                height: 17,
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-danger-solid)',
                color: 'var(--color-on-solid)',
                fontSize: 10,
              }}
            >
              {friend.unread > 99 ? '99+' : friend.unread}
              <span className="sr-only"> mensagens não lidas</span>
            </span>
          )}
        </>
      )}
    </button>
  );
}

function HashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M6 2.5L4.8 13.5M11.2 2.5L10 13.5M2.8 6h10.4M2.2 10h10.4"
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function VoiceIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8.6 3.2v9.6L5.4 10.2H3.2a.8.8 0 01-.8-.8V6.6a.8.8 0 01.8-.8h2.2L8.6 3.2z"
            stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M11.2 6.1a2.8 2.8 0 010 3.8" stroke="currentColor"
            strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
